import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tabs, Tag, message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getAssetById, deleteAsset } from '@/api/assets';
import type { Asset } from '@/types/resources/asset';
import { useAuth } from '@/contexts/AuthContext';
import { AssetModal } from '@/components/features/resources/assets/AssetModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import ActivityLogList from '@/components/common/ActivityLogList';
import dayjs from 'dayjs';
import type { Money } from '@/types/common';

const { Title } = Typography;

const AssetDetails: React.FC = () => {
  const { t } = useTranslation(['assets', 'common']);
  const { firestoreUser } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const state = location.state as { asset: Asset } | null;

  // State
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isDemoMode = firestoreUser?.workspace?.slug === 'demo-workspace';

  // Queries
  const { data: asset, isLoading, isError } = useQuery({
    queryKey: ['assets', id],
    queryFn: () => {
      if (!firestoreUser || !id) throw new Error('User or Asset ID not available');
      return getAssetById(id, firestoreUser);
    },
    enabled: !!id && (isDemoMode || !state?.asset) && !!firestoreUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteAsset(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('assets:messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      navigate('/resources/assets');
    },
    onError: (error) => {
      message.error(t('assets:messages.deleteError'));
      console.error('Error deleting asset:', error);
    }
  });

  // Use state data if available, otherwise use fetched data
  const displayAsset = isDemoMode ? asset : (state?.asset || asset);

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !displayAsset) {
    return <Card>{t('assets:notFound')}</Card>;
  }

  const handleEdit = () => {
    setIsModalVisible(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const formatMoney = (money: Money) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: money.currency || 'INR'
    }).format(money.amount);
  };

  const formatValue = (key: keyof Asset, value: any) => {
    if (key === 'purchaseDate') {
      return value ? dayjs(value).format('DD/MM/YYYY') : t('common:none');
    }
    if (key === 'status') {
      return (
        <Tag color={
          value === 'new' ? 'green' : 
          value === 'good' ? 'blue' : 
          value === 'fair' ? 'orange' : 
          value === 'poor' ? 'red' : 
          value === 'damaged' ? 'red' :
          value === 'active' ? 'green' : 
          value === 'maintenance' ? 'orange' : 
          value === 'retired' ? 'red' : 
          value === 'lost' ? 'red' : 'default'
        }>
          {t(`assets:status.${value}`, value as string)}
        </Tag>
      );
    }
    if (key === 'category') {
      return <Tag color="blue">{t(`assets:categories.${value}`, value as string)}</Tag>;
    }
    if (key === 'purchasePrice') {
      if (!value) return t('common:none');
      if (typeof value === 'number') {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR'
        }).format(value);
      }
      if (value.amount) {
        return formatMoney(value);
      }
      return t('common:none');
    }
    return value?.toString() || t('common:none');
  };

  const tabItems = [
    {
      key: 'overview',
      label: t('common:details'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('assets:fields.name')}>
              {displayAsset.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.category')}>
              {formatValue('category', displayAsset.category)}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.status')}>
              {formatValue('status', displayAsset.status)}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.location')}>
              {displayAsset.location || t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.block')}>
              {displayAsset.block?.name || t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.floor')}>
              {displayAsset.floor?.name || t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.amenity')}>
              {displayAsset.amenity?.name || t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.employee')}>
              {displayAsset.employee?.name || t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.vendor')}>
              {displayAsset.vendor?.name || t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.purchaseDate')}>
              {formatValue('purchaseDate', displayAsset.purchaseDate)}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.purchasePrice')}>
              {formatValue('purchasePrice', displayAsset.purchasePrice)}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.notes')}>
              {displayAsset.notes || t('common:none')}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'specifications',
      label: t('assets:sections.specifications'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('assets:fields.brand')}>
              {displayAsset.specifications?.brand || t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.model')}>
              {displayAsset.specifications?.model || t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.serialNumber')}>
              {displayAsset.specifications?.serialNumber || t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.dimensions')}>
              {displayAsset.specifications?.dimensions || t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.weight')}>
              {displayAsset.specifications?.weight ? `${displayAsset.specifications.weight} kg` : t('common:none')}
            </Descriptions.Item>
            <Descriptions.Item label={t('assets:fields.color')}>
              {displayAsset.specifications?.color || t('common:none')}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'warranty',
      label: t('assets:sections.warranty'),
      children: (
        <Card>
          {displayAsset.warranty ? (
            <Descriptions bordered column={1}>
              <Descriptions.Item label={t('assets:fields.provider')}>
                {displayAsset.warranty.provider}
              </Descriptions.Item>
              <Descriptions.Item label={t('assets:fields.startDate')}>
                {displayAsset.warranty.startDate ? dayjs(displayAsset.warranty.startDate).format('DD/MM/YYYY') : t('common:none')}
              </Descriptions.Item>
              <Descriptions.Item label={t('assets:fields.endDate')}>
                {displayAsset.warranty.endDate ? dayjs(displayAsset.warranty.endDate).format('DD/MM/YYYY') : t('common:none')}
              </Descriptions.Item>
              <Descriptions.Item label={t('assets:fields.details')}>
                {displayAsset.warranty.details}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              {t('assets:noWarranty')}
            </div>
          )}
        </Card>
      ),
    },
    {
      key: 'activityLog',
      label: t('common:activityLog'),
      children: (
        <Card>
          <ActivityLogList activityRecords={Array.isArray(displayAsset.activityRecords) ? displayAsset.activityRecords : []} />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/resources/assets')}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>{displayAsset.name}</Title>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Tabs items={tabItems} />

      {/* Modals */}
      <AssetModal
        open={isModalVisible}
        asset={displayAsset}
        onSuccess={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        category={displayAsset.category}
      />

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (displayAsset.id) {
            deleteMutation.mutate(displayAsset.id);
          }
        }}
        title={t('assets:messages.deleteConfirm')}
      />
    </div>
  );
};

export default AssetDetails; 